import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Params, Router, RouterOutlet } from '@angular/router';
import { HippoFullscreenComponent } from '@hippo/components/fullscreen';
import { HippoLoadingBarComponent } from '@hippo/components/loading-bar';
import {
    HippoNavigationService,
    HippoVerticalNavigationComponent,
} from '@hippo/components/navigation';
import { HippoMediaWatcherService } from '@hippo/services/media-watcher';
import {
    provideTranslocoScope,
    TranslocoModule,
    TranslocoService,
} from '@jsverse/transloco';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { HeadlineService } from 'app/core/services/headline.service';
import { PermissionService } from 'app/core/services/permission.service';
import { UserService } from 'app/core/services/user.service';
import { ZoomLevelService } from 'app/core/services/zoom-level.service';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { MyDataService } from 'app/modules/admin/team/my-data/my-data.service';
import { environment } from 'environments/environment';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'classic-layout',
    templateUrl: './classic.component.html',
    styleUrls: ['./classic.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        HippoLoadingBarComponent,
        HippoVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        LanguagesComponent,
        HippoFullscreenComponent,
        UserComponent,
        RouterOutlet,
        MatTooltipModule,
        MatCheckboxModule,
        FormsModule,
        TranslocoModule,
        CommonModule,
    ],
    providers: [
        provideTranslocoScope('group'),
        provideTranslocoScope('p_wpo'),
        provideTranslocoScope('p_usd'),
        provideTranslocoScope('p_usl'),
        provideTranslocoScope('p_nfd'),
        provideTranslocoScope('p_pgo'),
        provideTranslocoScope('p_stp'),
        provideTranslocoScope('page'),
        provideTranslocoScope('codext'),
        provideTranslocoScope('p_ore'),
        provideTranslocoScope('p_hev'),
        provideTranslocoScope('p_vne'),
        provideTranslocoScope('p_ete'),
        provideTranslocoScope('p_msr'),
        provideTranslocoScope('p_stt'),
        provideTranslocoScope('p_uho'),
        provideTranslocoScope('p_vhu'),
        // provideTranslocoScope('p_uhh'),
    ],
})
export class ClassicLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall!: boolean;
    navigation!: Navigation;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    environment = environment;
    navigationOpened!: boolean;

    /**
     * Constructor
     */
    constructor(
        private _router: Router,
        private _navigationService: NavigationService,
        private _hippoMediaWatcherService: HippoMediaWatcherService,
        private _hippoNavigationService: HippoNavigationService,
        public headlineService: HeadlineService,
        private myDataService: MyDataService,
        private permissionService: PermissionService,
        public translocoService: TranslocoService,
        private route: ActivatedRoute,
        public userService: UserService,
        private zoomLevelService: ZoomLevelService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit() {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                let appStarted = false;
                if (!this.navigation) {
                    appStarted = true;
                }
                this.navigation = navigation;
                this.loadToggle(appStarted);
            });

        // Subscribe to media changes
        this._hippoMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
                this.navigationOpened = !this.isScreenSmall;
            });
        this.navigationSub();
    }

    loadToggle(appStarted: boolean) {
        if (appStarted) {
            this.navigation.default.forEach(item => {
                if (item.number !== undefined) {
                    item.show =
                        this.myDataService.openedMenus[item.number] ?? false;
                    this._navigationService.openedMenus[item.number] =
                        item.show;
                }
            });
            if (this.permissionService.hasPermissions(['news_feed_read'])) {
                const newsFeed = this.navigation.default.find(
                    nav => nav.number === 0
                );
                if (newsFeed) newsFeed.show = true;
            } else {
                const systemNav = this.navigation.default.find(
                    nav => nav.number === 11
                );
                if (systemNav) systemNav.show = true;
            }
        } else {
            this.navigation.default.forEach(item => {
                if (item.number !== undefined) {
                    item.show =
                        this._navigationService.openedMenus[item.number] ??
                        false;
                }
            });
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._hippoNavigationService.getComponent<HippoVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
            this.navigationOpened = navigation.opened;
        }
    }

    navigationSub() {
        this.route.params
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((params: Params) => {
                console.log(params['oldSystem']);
                // this.headlineService.showIframe = params['oldSystem'];
                // this.toggleOldSystem();
            });
    }

    openInspector($event: Event) {
        $event.preventDefault();
        $event.stopPropagation();
        this._router.navigate(['/inspector']);
    }
}
